/**
 * Home.jsx
 * Executive landing page — positions Robert as a technology leader.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedinIn, faGithub } from '@fortawesome/free-brands-svg-icons';
import { faFilePdf, faEnvelope, faArrowRight, faUsers, faArrowTrendUp, faRobot, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';

import {useNavHelper} from '#hooks/useNavHelper';
import config from '#config/server';
import { PostsSummary } from '#components/PostsSummary';

const timeline = [
	{ role: 'Vice President of Information Technology', org: 'Our Town America', meta: 'Jun 2026 – Present · Clearwater, FL' },
	{ role: 'Director of Software Engineering', org: 'Our Town America', meta: 'Oct 2025 – Jun 2026' },
	{ role: 'Lead Software Engineer', org: 'Our Town America', meta: 'Jun 2015 – Oct 2025' },
	{ role: 'Product Manager', org: 'Carnet Marketing & Customer Track', meta: 'Jun 2010 – Jun 2015' },
];

export function Home(){
	const navigate = useNavHelper();
	const img = p => `${config.base_url}assets/images/${p}`;

	return (<>
		{/* ---------- Hero ---------- */}
		<section className="hero">
			<div className="row align-items-center g-5">
				<div className="col-lg-7 order-2 order-lg-1">
					<p className="hero-role">TECHNOLOGY LEADER & LIFELONG BUILDER</p>
					<h1 className="hero-name">Robert Parham</h1>
					<p className="hero-lead">
						A builder at heart with 20+ years across software engineering, DevOps, and technology leadership — I've written the code, shipped the products, built the teams, and <span className="accent">set the strategy</span>.
					</p>
					<div className="cta-row">
						<button className="btn btn-navy" onClick={e=>navigate(e, '/resume')}>
							<FontAwesomeIcon icon={faFilePdf} /> Résumé
						</button>
						<a className="btn btn-gold" href="https://www.linkedin.com/in/robert-parham/" target="_blank" rel="noopener">
							<FontAwesomeIcon icon={faLinkedinIn} /> LinkedIn
						</a>
						<a className="btn btn-outline-navy" href="https://github.com/Pamblam" target="_blank" rel="noopener">
							<FontAwesomeIcon icon={faGithub} /> GitHub
						</a>
					</div>
				</div>
				<div className="col-lg-5 order-1 order-lg-2">
					<div className="hero-portrait-frame">
						<div className="hero-portrait" style={{aspectRatio:'4 / 5'}}>
							<img src={img('rob-hero.jpg')} alt="Robert Parham" />
						</div>
					</div>
				</div>
			</div>
		</section>

		<hr className="section-sep" />

		{/* ---------- Trajectory ---------- */}
		<section>
			<div className="row g-5">
				<div className="col-lg-5">
					<div className="section-head">
						<span className="eyebrow">Trajectory</span>
						<h2>A record of earned trust</h2>
					</div>
					<p className="text-muted" style={{maxWidth:'22rem'}}>
						A track record of modernizing how teams build software — SVN → Git migrations, CI/CD from the ground up, technology-governance frameworks, and standards that stuck.
					</p>
					<button className="btn btn-outline-navy mt-2" onClick={e=>navigate(e, '/resume')}>
						Full résumé <FontAwesomeIcon icon={faArrowRight} />
					</button>
				</div>
				<div className="col-lg-7">
					<div className="timeline">
						{timeline.map((t, i)=>(
							<div className="tl-item" key={i}>
								<div className="tl-role">{t.role}</div>
								<div className="tl-org">{t.org}</div>
								<div className="tl-meta">{t.meta}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>

		<hr className="section-sep" />

		{/* ---------- About band (secondary photo) ---------- */}
		<section>
			<div className="about-band">
				<div className="about-photo text-center">
					<img src={img('rob-tie.jpg')} alt="Robert Parham" />
					<div className="cap">Tampa Bay, FL</div>
				</div>
				<div>
					<div className="quote-mark">“</div>
					<h3 className="fw-display">Technology only matters when it moves the business.</h3>
					<p>
						I partner with executive peers to align technology initiatives with growth objectives and
						P&amp;L goals — surfacing cost-optimization opportunities, raising release quality, and building
						teams that keep delivering after I step back. Rooted in Tampa Bay and active with
						Tampa Devs and the National Society of Leadership &amp; Success.
					</p>
				</div>
			</div>
		</section>

		<hr className="section-sep" />

		{/* ---------- Writing / thought leadership ---------- */}
		<section>
			<div className="section-head">
				<span className="eyebrow">Writing</span>
				<h2>Notes on engineering &amp; leadership</h2>
			</div>
			<PostsSummary noResultsText="New writing is on the way." />
		</section>
	</>);
}
